import { ExceptionDimensions, ResponseCode, logAnalyticsEvent } from '@vestico/utils';

import { ProductWidget, RecordEventBody, RecordEventResponse, RecordLoadEventBody, RecordVisitEventBody, VisitEventType } from '@vestico/model';

import { AuthorizedRequestInfo, ProductConfiguration, VisitsApi } from '../model/api';
import { AbstractApiClient } from './apiClient';
import { createAPIRequestURL, fetchRequest, getClientDetails } from './helpers';

export class VisitsApiClient extends AbstractApiClient implements VisitsApi {
  public async recordImpression(
    widgetEntryID: string,
    user: string | undefined,
    authRequestInfo: AuthorizedRequestInfo
  ): Promise<string | undefined> {
    // Don't send server requests when we already have a user and are in preview mode
    if (this.isPreview && user) {
      return user;
    }

    const body: RecordLoadEventBody = {
      client: this.clientId,
      products: undefined,
      user: user,
      widgetEntryID,
      isPreview: this.isPreview,
      clientDetails: await getClientDetails(),
      experiments: this.clientExperiments,
    };

    return this.recordEvent('/track/view', body, this.clientId, authRequestInfo);
  }

  public async recordLoadEvent(
    config: ProductConfiguration | ProductConfiguration[] | undefined,
    user: string | undefined,
    isShowcase: boolean | undefined,
    authRequestInfo: AuthorizedRequestInfo
  ): Promise<string | undefined> {
    // Don't send server requests when we already have a user and are in preview mode
    if (this.isPreview && user) {
      return user;
    }

    // Extract product UIDs from config (single or array)
    let productUIDs: string[] | undefined;
    if (config) {
      if (Array.isArray(config)) {
        productUIDs = config.map((c) => c.productUID).filter(Boolean);
      } else {
        productUIDs = config.productUID ? [config.productUID] : undefined;
      }
    }

    const body: RecordLoadEventBody = {
      client: this.clientId,
      products: productUIDs,
      user: user,
      isShowcase,
      isPreview: this.isPreview,
      clientDetails: await getClientDetails(),
      experiments: this.clientExperiments,
    };

    return this.recordEvent('/track/view', body, this.clientId, authRequestInfo);
  }

  public async recordVisitEvent(
    type: VisitEventType,
    items: ProductWidget[] | undefined,
    user: string | undefined,
    authRequestInfo: AuthorizedRequestInfo
  ): Promise<string | undefined> {
    const body: RecordVisitEventBody = {
      client: this.clientId,
      products: items?.map((i) => i.documentID),
      user: user,
      isPreview: this.isPreview,
      clientDetails: await getClientDetails(),
      type,
      experiments: this.clientExperiments,
    };

    return this.recordEvent('/track/visit', body, this.clientId, authRequestInfo);
  }

  async recordEvent(
    apiEndpoint: string,
    body: RecordEventBody,
    clientId: string,
    authRequestInfo: AuthorizedRequestInfo
  ): Promise<string | undefined> {
    if (!authRequestInfo || !clientId) {
      console.error(`record event ${apiEndpoint} missing required entries`);
      logAnalyticsEvent({
        category: 'exception',
        event: `record-event-precondition`,
        name: apiEndpoint,
        dimensions: { provider: clientId } as ExceptionDimensions,
      });
      return undefined;
    }

    const fetchURL = await createAPIRequestURL(authRequestInfo, apiEndpoint);

    if (!fetchURL) {
      console.error(`cannot create record event ${apiEndpoint} URL`);

      logAnalyticsEvent({
        category: 'exception',
        event: `record-load-event-precondition`,
        name: `Cannot create URL ${apiEndpoint}`,
        dimensions: { provider: clientId } as ExceptionDimensions,
      });
      return undefined;
    }

    try {
      const result = await fetchRequest({
        url: fetchURL.toString(),
        method: 'POST',
        body,
        iOSBundleIdentifier: this.iOSBundleIdentifier,
        authRequestInfo,
        keepalive: true,
      });

      if (result.status !== ResponseCode.success) {
        console.error(`Record load event returned error ${result.status}`);
        logAnalyticsEvent({
          category: 'exception',
          event: `record-load-event`,
          name: `${result.status}`,
          dimensions: { provider: clientId, description: await result.text() } as ExceptionDimensions,
        });
        return undefined;
      }

      const response = (await result.json()) as RecordEventResponse;
      return response.user;
    } catch (error) {
      console.error(error);
      logAnalyticsEvent({
        category: 'exception',
        event: `record-load-event req`,
        name: error.message,
      });
      return undefined;
    }
  }
}
